// Tiny inline icon set — Lucide SVG paths (MIT, from lucide.dev). Copied inline so we ship no
// external font, no CDN, no npm dep. Every icon renders as a 24×24 viewBox stroked in
// `currentColor` at stroke-width 1.5 — so they inherit the theme colour and align pixel-perfect
// with the label next to them. To add a new icon: paste its path snippet from lucide.dev into
// PATHS below (keep the leading + trailing whitespace off; the SVG wrapper adds the outer tags).
//
// Usage in a lit template:
//   import { icon } from '../shared/icons.js';
//   ${icon('layout-dashboard')}            // 20px default (nav)
//   ${icon('users', { size: 16 })}         // custom size
//
// Because we return an `svg` string, drop it into html`${icon('x')}` and the template rewriter
// hands it back as-is — no innerHTML, no `unsafeHTML` helper needed.
import { html } from './t4.js';

// Lucide paths (24×24 viewBox, stroke = currentColor, stroke-width = 1.5, no fill).
// Kept as inner-SVG fragments so the wrapper below can standardise every stroke/size attribute.
const PATHS = {
  'layout-dashboard':
    '<rect width="7" height="9" x="3" y="3" rx="1"/>' +
    '<rect width="7" height="5" x="14" y="3" rx="1"/>' +
    '<rect width="7" height="9" x="14" y="12" rx="1"/>' +
    '<rect width="7" height="5" x="3" y="16" rx="1"/>',

  'bug':
    '<path d="m8 2 1.88 1.88"/>' +
    '<path d="M14.12 3.88 16 2"/>' +
    '<path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"/>' +
    '<path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6"/>' +
    '<path d="M12 20v-9"/>' +
    '<path d="M6.53 9C4.6 8.8 3 7.1 3 5"/>' +
    '<path d="M6 13H2"/>' +
    '<path d="M3 21c0-2.1 1.7-3.9 3.8-4"/>' +
    '<path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"/>' +
    '<path d="M22 13h-4"/>' +
    '<path d="M17.2 17c2.1.1 3.8 1.9 3.8 4"/>',

  'shield-check':
    '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.79 17 5 19 5a1 1 0 0 1 1 1z"/>' +
    '<path d="m9 12 2 2 4-4"/>',

  'layers':
    '<path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/>' +
    '<path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/>' +
    '<path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/>',

  'globe':
    '<circle cx="12" cy="12" r="10"/>' +
    '<path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>' +
    '<path d="M2 12h20"/>',

  'languages':
    '<path d="m5 8 6 6"/>' +
    '<path d="m4 14 6-6 2-3"/>' +
    '<path d="M2 5h12"/>' +
    '<path d="M7 2h1"/>' +
    '<path d="m22 22-5-10-5 10"/>' +
    '<path d="M14 18h6"/>',

  'arrow-left-right':
    '<path d="M8 3 4 7l4 4"/>' +
    '<path d="M4 7h16"/>' +
    '<path d="m16 21 4-4-4-4"/>' +
    '<path d="M20 17H4"/>',

  'building-2':
    '<path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/>' +
    '<path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/>' +
    '<path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/>' +
    '<path d="M10 6h4"/>' +
    '<path d="M10 10h4"/>' +
    '<path d="M10 14h4"/>' +
    '<path d="M10 18h4"/>',

  'users':
    '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>' +
    '<circle cx="9" cy="7" r="4"/>' +
    '<path d="M22 21v-2a4 4 0 0 0-3-3.87"/>' +
    '<path d="M16 3.13a4 4 0 0 1 0 7.75"/>',

  'credit-card':
    '<rect width="20" height="14" x="2" y="5" rx="2"/>' +
    '<line x1="2" x2="22" y1="10" y2="10"/>',

  'handshake':
    '<path d="m11 17 2 2a1 1 0 1 0 3-3"/>' +
    '<path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4"/>' +
    '<path d="m21 3 1 11h-2"/>' +
    '<path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3"/>' +
    '<path d="M3 4h8"/>',

  'more-horizontal':
    '<circle cx="12" cy="12" r="1"/>' +
    '<circle cx="19" cy="12" r="1"/>' +
    '<circle cx="5" cy="12" r="1"/>',

  // ── tenant nav additions ─────────────────────────────────────────────────
  'file-text':
    '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/>' +
    '<path d="M14 2v4a2 2 0 0 0 2 2h4"/>' +
    '<path d="M10 9H8"/>' +
    '<path d="M16 13H8"/>' +
    '<path d="M16 17H8"/>',

  'landmark':
    '<line x1="3" x2="21" y1="22" y2="22"/>' +
    '<line x1="6" x2="6" y1="18" y2="11"/>' +
    '<line x1="10" x2="10" y1="18" y2="11"/>' +
    '<line x1="14" x2="14" y1="18" y2="11"/>' +
    '<line x1="18" x2="18" y1="18" y2="11"/>' +
    '<polygon points="12 2 20 7 4 7"/>',

  'clipboard-check':
    '<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/>' +
    '<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>' +
    '<path d="m9 14 2 2 4-4"/>',

  'receipt':
    '<path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/>' +
    '<path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/>' +
    '<path d="M12 17.5v-11"/>',

  'package':
    '<path d="M16.5 9.4 7.55 4.24"/>' +
    '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>' +
    '<polyline points="3.29 7 12 12 20.71 7"/>' +
    '<line x1="12" x2="12" y1="22" y2="12"/>',

  'folder':
    '<path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>',

  'rotate-ccw':
    '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>' +
    '<path d="M3 3v5h5"/>',

  'contact':
    '<path d="M17 18a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2"/>' +
    '<rect width="18" height="18" x="3" y="4" rx="2"/>' +
    '<circle cx="12" cy="10" r="2"/>' +
    '<line x1="8" x2="8" y1="2" y2="4"/>' +
    '<line x1="16" x2="16" y1="2" y2="4"/>',

  'phone':
    '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>',

  'mail':
    '<rect width="20" height="16" x="2" y="4" rx="2"/>' +
    '<path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>',

  'bar-chart-3':
    '<path d="M3 3v18h18"/>' +
    '<path d="M18 17V9"/>' +
    '<path d="M13 17V5"/>' +
    '<path d="M8 17v-3"/>',

  'settings':
    '<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>' +
    '<circle cx="12" cy="12" r="3"/>',

  'copy':
    '<rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>' +
    '<path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>',
  'external-link':                                    // pop a company out into its own window
    '<path d="M15 3h6v6"/><path d="M10 14 21 3"/>' +
    '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>',

  // ── reports icons ────────────────────────────────────────────────────────
  'scale':
    '<path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/>' +
    '<path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/>' +
    '<path d="M7 21h10"/>' +
    '<path d="M12 3v18"/>' +
    '<path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/>',

  'trending-up':
    '<polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>' +
    '<polyline points="16 7 22 7 22 13"/>',

  'trending-down':
    '<polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/>' +
    '<polyline points="16 17 22 17 22 11"/>',

  'book-open-check':
    '<path d="M8 3H2v15h7c1.7 0 3 1.3 3 3V7c0-2.2-1.8-4-4-4Z"/>' +
    '<path d="m16 12 2 2 4-4"/>' +
    '<path d="M12 21c0-2.2 1.8-4 4-4h6V4c0-.6-.4-1-1-1h-5c-1.7 0-3 1.3-3 3"/>',

  'clock':
    '<circle cx="12" cy="12" r="10"/>' +
    '<polyline points="12 6 12 12 16 14"/>',

  'book-open':
    '<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>' +
    '<path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>',

  'arrow-up-down':
    '<path d="m21 16-4 4-4-4"/>' +
    '<path d="M17 20V4"/>' +
    '<path d="m3 8 4-4 4 4"/>' +
    '<path d="M7 4v16"/>',

  'list-ordered':
    '<line x1="10" x2="21" y1="6" y2="6"/>' +
    '<line x1="10" x2="21" y1="12" y2="12"/>' +
    '<line x1="10" x2="21" y1="18" y2="18"/>' +
    '<path d="M4 6h1v4"/>' +
    '<path d="M4 10h2"/>' +
    '<path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/>',

  'percent':
    '<line x1="19" x2="5" y1="5" y2="19"/>' +
    '<circle cx="6.5" cy="6.5" r="2.5"/>' +
    '<circle cx="17.5" cy="17.5" r="2.5"/>',

  'check-check':
    '<path d="M18 6 7 17l-5-5"/>' +
    '<path d="m22 10-7.5 7.5L13 16"/>',

  'tag':
    '<path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/>' +
    '<circle cx="7.5" cy="7.5" r=".5" fill="currentColor"/>',

  // ── activity timeline / doc peek (documents.js PEEK_ICONS) ───────────────
  'send':
    '<path d="m22 2-7 20-4-9-9-4Z"/>' +
    '<path d="M22 2 11 13"/>',
  'alert-triangle':
    '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>' +
    '<path d="M12 9v4"/>' +
    '<path d="M12 17h.01"/>',
  'eye':
    '<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>' +
    '<circle cx="12" cy="12" r="3"/>',
  'dollar-sign':
    '<line x1="12" x2="12" y1="2" y2="22"/>' +
    '<path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>',
  'pencil':
    '<path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/>' +
    '<path d="m15 5 4 4"/>',
  'ban':
    '<circle cx="12" cy="12" r="10"/>' +
    '<path d="m4.9 4.9 14.2 14.2"/>',
  'check':
    '<polyline points="20 6 9 17 4 12"/>',
  'x':
    '<path d="M18 6 6 18"/>' +
    '<path d="m6 6 12 12"/>',

  // ── app-bar chrome ──────────────────────────────────────────────────────
  'sun':
    '<circle cx="12" cy="12" r="4"/>' +
    '<path d="M12 2v2"/>' +
    '<path d="M12 20v2"/>' +
    '<path d="m4.93 4.93 1.41 1.41"/>' +
    '<path d="m17.66 17.66 1.41 1.41"/>' +
    '<path d="M2 12h2"/>' +
    '<path d="M20 12h2"/>' +
    '<path d="m6.34 17.66-1.41 1.41"/>' +
    '<path d="m19.07 4.93-1.41 1.41"/>',
  'moon':
    '<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z"/>',
  'lock':
    '<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>' +
    '<path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
  'briefcase':
    '<rect width="20" height="14" x="2" y="7" rx="2" ry="2"/>' +
    '<path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>',
  'download':
    '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>' +
    '<polyline points="7 10 12 15 17 10"/>' +
    '<line x1="12" x2="12" y1="15" y2="3"/>',
  'printer':
    '<polyline points="6 9 6 2 18 2 18 9"/>' +
    '<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>' +
    '<rect width="12" height="8" x="6" y="14"/>',
  'trash-2':
    '<path d="M3 6h18"/>' +
    '<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>' +
    '<path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>' +
    '<line x1="10" x2="10" y1="11" y2="17"/>' +
    '<line x1="14" x2="14" y1="11" y2="17"/>',
  'plus':
    '<path d="M12 5v14"/>' +
    '<path d="M5 12h14"/>',
  'chevron-left':
    '<path d="m15 18-6-6 6-6"/>',
  'chevron-right':
    '<path d="m9 18 6-6-6-6"/>',
  'chevron-down':
    '<path d="m6 9 6 6 6-6"/>',
  'chevron-up':
    '<path d="m18 15-6-6-6 6"/>',
  'wallet':
    '<path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"/>' +
    '<path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"/>',
};

// Standard SVG wrapper — one place to change stroke width, cap, join. viewBox is 24×24 for every
// Lucide icon; `size` scales the rendered pixels. `class` lets you nudge alignment (e.g. .nav-ico)
// without giving up currentColor.
export function iconSvg(name, size = 20, extraClass = '') {
  const path = PATHS[name];
  if (!path) return '';
  const cls = extraClass ? ` class="${extraClass}"` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"`
    + ` viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"`
    + ` stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"${cls}>${path}</svg>`;
}

// Convenience wrapper that returns a lit template — same call site as before (${icon('users')}).
// Uses `unsafeStatic` semantics via a helper span whose innerHTML is set by tina4's html raw path.
// Simpler here: return an object t4 renders via its markup() escape hatch — but the smallest
// approach is a plain string embedded as text; tina4's html rewriter treats a `<svg…>` prefix as
// raw markup. If that ever changes, wrap this in the framework's raw-html helper of the day.
export function icon(name, opts) {
  // Gradual-migration escape hatch: if `name` isn't one of our Lucide keys, fall back to
  // rendering it as raw text so screens still on the old Unicode-glyph icons (e.g. legacy
  // tenant bottom bar entries like '⌂') keep working while the migration proceeds file by
  // file. Once every icon: value is a Lucide key, this branch can go.
  if (!name || !PATHS[name]) return name || '';
  const size = (opts && opts.size) || 20;
  const cls = (opts && opts.class) || '';
  const raw = iconSvg(name, size, cls);
  // t4's `html` tagged template renders DOM nodes for us; we hand it a pre-parsed <svg>
  // element via a <template> so the SVG is treated as markup, not escaped text.
  const tmpl = document.createElement('template');
  tmpl.innerHTML = raw;
  return html`${tmpl.content.firstElementChild}`;
}
